import qs from 'qs';
import { apiUrl } from 'services/fetch';
import statePostalFips from 'config/statePostalFips';

/**
 * Given information about a view, constructs the appropriate link to
 * download the data via the API in the requested format.
 * @param {object} options - An object representing view state.
 * @param {string} options.dataView - Required. The data view to use.
 * @param {array} options.geos - Optional. A list of FIPS codes and/or state IDs to filter by.
 * @param {string} options.primaryView - Optional. The primary data view (e.g., map).
 * @param {string} options.secondaryView - Optional. The secondary data view (e.g., county).
 * @param {string} options.slug - Required. The indicator slug to use.
 * @param {number|string} options.timeframe - Optional. Timeframe value to filter by.
 * @param {string} options.type - Required. The output format for the download (csv, image).
 * @returns {string} - The compiled download URL for the requested format.
 */
export default function downloadLink({
  dataView,
  geos = [],
  primaryView = '',
  secondaryView = '',
  slug,
  timeframe = '',
  type,
}) {
  // Construct the timeframe segment conditionally.
  const timeSegment = timeframe ? `/${timeframe}/` : '/';

  // Filter geos to remove top-level geos based on view.
  const filteredGeos = 'csv' === type ? geos.filter((geo) =>
    ('usdata' === dataView && 'US' !== geo) ||
    ('international' === dataView && 'WORLD' !== geo)
  ) : geos;

  // Dereference geos, if necessary.
  const dereferencedGeos = {};
  if (filteredGeos.length) {
    /**
     * Take the array of state IDs and FIPS codes (e.g., [AL, 1]) and convert it
     * to an object with the state IDs as keys and the FIPS codes as values,
     * which can then be used to perform lookups of FIPS codes by state ID.
     */
    const fipsMap = statePostalFips.reduce((acc, [stateId, fipsCode]) => ({
      ...acc,
      [stateId]: fipsCode,
    }), {});

    // Dereference geographies.
    dereferencedGeos.geos = filteredGeos
      .map((geo) =>
        (geo in fipsMap ? fipsMap[geo] : geo)
      )
      .join(',');
  }

  // Construct query string arguments as an object.
  const queryArguments = {
    ...dereferencedGeos,
    ...(primaryView) ? { primaryView } : {},
    ...(secondaryView) ? { secondaryView } : {},
  };

  // Get the query string, if necessary.
  const query = Object.keys(queryArguments).length ?
    `?${qs.stringify(queryArguments)}` : '';

  // Compile the URL.
  return `${apiUrl()}/download/${dataView}/indicator/${slug}${timeSegment}${type}${query}`;
}



// WEBPACK FOOTER //
// ./app/services/downloadLink.js